import lodash from "lodash";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useGroupsFilterContext } from "../../../../providers/groups/groups-filter-provider";
import { IGroupOrManufacturer } from "../../../domain/group/group-or-manufacturer";
import { IPaginationMeta } from "../../../domain/pagination/pagination-meta";
import { IPaginationMetaModel } from "../../../models/util/pagination-meta.model";
import { IGetGroupsFilter } from "../../../repositories/models/group/get-group.model";
import { useGetGroupsAndManufacturersWithFiltersQuery } from "../../../repositories/queries/group/get-groups-and-manufacturers.query";
import { useUpdateGroupActive } from "../../../repositories/queries/group/update-group-active.query";

interface IGroupAndManufacturerContextType {
  isLoading: boolean;
  tablePagination: IPaginationMetaModel;
  setTablePagination: (pagination: IPaginationMetaModel) => void;
  filter: IGetGroupsFilter;
  setFilter: (filters: IGetGroupsFilter) => void;
  groups: IGroupOrManufacturer[];
  refetchGroups: () => Promise<void>;
  updateGroupActive: (groupId: number, active: boolean) => Promise<void>;
  isFilterDirty: boolean;
  resetFilter: () => void;
}

const GroupAndManufacturerContext = createContext<IGroupAndManufacturerContextType>(
  {} as IGroupAndManufacturerContextType,
);

export const GroupAndManufacturerProvider: React.FC<{ children?: React.ReactNode }> = (props) => {
  const value = useGroupAndManufacturerProvider();
  return <GroupAndManufacturerContext.Provider value={value}>{props.children}</GroupAndManufacturerContext.Provider>;
};

export const useGroupAndManufacturerContext = () => {
  return useContext(GroupAndManufacturerContext);
};

const useGroupAndManufacturerProvider = (): IGroupAndManufacturerContextType => {
  const { filter, setFilter } = useGroupsFilterContext();

  const [tablePagination, setTablePagination] = useState<IPaginationMeta>({
    numberOfRecords: 0,
    pageSize: 50,
    page: 0,
    numberOfPages: 0,
  });
  const { data, isLoading: areGroupsLoading, refetch } = useGetGroupsAndManufacturersWithFiltersQuery(
    tablePagination,
    filter,
  );
  const { isPending: isUpdateGroupActiveLoading, mutateAsync: updateGroupActiveQuery } = useUpdateGroupActive();

  const groups = data?.items ?? [];

  useEffect(() => {
    const numberOfRecords = data?.meta?.numberOfRecords;
    if (numberOfRecords !== undefined) {
      setTablePagination((prev) => {
        if (prev.numberOfRecords === numberOfRecords) return prev;
        return { ...prev, numberOfRecords };
      });
    }
  }, [data]);

  const refetchGroups = async () => {
    await refetch();
  };

  const updateGroupActive = async (groupId: number, active: boolean): Promise<void> => {
    await updateGroupActiveQuery({
      id: groupId,
      isActive: active,
    });
  };

  const isLoading = areGroupsLoading || isUpdateGroupActiveLoading;

  const isFilterDirty = useMemo(
    () =>
      !lodash.isEmpty(filter.active) ||
      !lodash.isEmpty(filter.groupTypes) ||
      !lodash.isEmpty(filter.searchQuery) ||
      !lodash.isEmpty(filter.contractTypes),
    [filter],
  );

  const resetFilter = () =>
    setFilter({
      active: undefined,
      searchQuery: undefined,
      groupTypes: [],
      contractTypes: undefined,
    });

  return {
    isLoading,
    tablePagination,
    setTablePagination,
    filter,
    setFilter,
    groups,
    refetchGroups,
    isFilterDirty,
    resetFilter,
    updateGroupActive,
  };
};
